import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { NbToastrService } from '@nebular/theme';
import { SubscribedComponent } from '../../subscribed.component';
import { IntakeAttachment, IntakeContact, IntakeMail, IntakeMailEvent } from '../../interfaces/intake';
import {
  IntakeService, INTAKE_DELETE_SILENT, intakeAttachmentPath, intakeContentGone, intakeErrorMessage, intakeHtmlPath,
} from '../../shared/intake.service';
import { NotifierService } from '../../shared/notifier.service';
import { AuthService } from '../../shared/auth.service';
import { TitleService } from '../../shared/title.service';
import { IntakeDatePipe } from '../intake-date/intake-date.pipe';

export interface IntakeAttachmentLink extends IntakeAttachment {
  href: string;
}

@Component({
  selector: 'bst-intake-mail-view',
  templateUrl: './intake-mail-view.component.html',
  styleUrls: ['./intake-mail-view.component.scss'],
  standalone: false
})
export class IntakeMailViewComponent extends SubscribedComponent implements OnInit {

  mail: IntakeMail = null;
  frameUrl: SafeResourceUrl = null;
  /** The mail HTML wrapped with the app font; the frame waits for it and uses frameUrl only if the fetch fails. */
  frameDoc: SafeHtml = null;
  frameFallback = false;
  /** The text part of a mail without HTML; interpolated, so it is escaped and never parsed as markup. */
  bodyText: string = null;
  attachments: IntakeAttachmentLink[] = [];
  contacts: IntakeContact[] = [];
  retentionDays: number = null;
  notFound = false;
  deleting = false;

  private loadGeneration = 0;
  private readonly dates: IntakeDatePipe;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private intake: IntakeService,
              private sanitizer: DomSanitizer,
              private notifier: NotifierService,
              private translate: TranslateService,
              private auth: AuthService,
              private toastr: NbToastrService,
              private titles: TitleService) {
    super();
    this.dates = new IntakeDatePipe(translate);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subs.push(this.route.paramMap.subscribe(params => void this.load(params.get('mailId'))));
  }

  /** Read once at activation, before the mail loads; the object form makes TitleService wait for the translations. */
  get headerTitle(): { title: string; translate: boolean } {
    return { title: 'intake.title', translate: true };
  }

  get canDelete(): boolean {
    return this.auth.isAdmin;
  }

  get contentGone(): boolean {
    return !!this.mail && intakeContentGone(this.mail);
  }

  /** Secondary header facts, kept out of the meta row: the received date's title. */
  get metaInfo(): string {
    if (!this.mail) { return ''; }
    const parts: string[] = [];
    if (this.mail.forwarder && this.mail.forwarder !== this.mail.sender) {
      parts.push(this.translate.instant('intake.mail.forwarded_by', { forwarder: this.mail.forwarder }));
    }
    if (this.mail.content_removed_on && !this.contentGone) {
      parts.push(this.translate.instant('intake.mail.kept_until', { date: this.dates.transform(this.mail.content_removed_on, 'day') }));
    }
    return parts.join(' · ');
  }

  /** Retention of the contacts, the Contacts heading's title rather than a visible line. */
  get contactsHint(): string {
    return this.retentionDays
      ? this.translate.instant('intake.mail.contacts_hint_days', { days: this.retentionDays })
      : this.translate.instant('intake.mail.contacts_hint');
  }

  /** History is the one closed section: a quiet toggle at the bottom of the details. */
  historyOpen = false;

  get logCount(): number { return this.mail?.events?.length ?? 0; }

  toggleHistory(): void {
    this.historyOpen = !this.historyOpen;
  }

  /** Who decided and with which prompt: kept off the visible line, in the entry's title. */
  logTip(e: IntakeMailEvent): string | null {
    const parts: string[] = [];
    if (!e.actor_name) { parts.push(this.translate.instant('intake.mail.log_parser')); }
    if (e.prompt_version) { parts.push(this.translate.instant('intake.mail.log_prompt', { version: e.prompt_version })); }
    return parts.length ? parts.join(' · ') : null;
  }

  async load(rawId: string | null): Promise<void> {
    const generation = ++this.loadGeneration;
    // intakeHtmlPath is the single numeric-id check; a bad id never reaches the API.
    if (intakeHtmlPath(rawId) === null) {
      this.clear();
      this.notFound = true;
      return;
    }
    this.loading.next(true);
    try {
      const mail = await this.intake.mail(Number(rawId)).catch(() => null);
      if (generation !== this.loadGeneration) { return; }
      if (!mail) {
        this.clear();
        this.notFound = true;
        return;
      }
      this.setMail(mail);
      this.retentionDays = await this.intake.addressQuietly().then(a => a?.retention_days ?? null, () => null);
    } finally {
      if (generation === this.loadGeneration) {
        this.loading.next(false);
      }
    }
  }

  /** Also the verdict panel's (mailChange) target: every action returns the fresh mail. */
  setMail(mail: IntakeMail): void {
    if (mail?.subject && mail.subject !== this.mail?.subject) { void this.titleWithSubject(mail.subject); }
    this.mail = mail;
    this.notFound = false;
    if (intakeContentGone(mail)) {
      this.frameUrl = null;
      this.bodyText = null;
      this.attachments = [];
      this.contacts = [];
      return;
    }
    const path = mail.has_html ? intakeHtmlPath(mail.id) : null;
    if (path && this.frameDocFor !== mail.id) { this.loadFrameDoc(mail.id); }
    this.frameUrl = path ? this.sanitizer.bypassSecurityTrustResourceUrl(path) : null;
    this.bodyText = path ? null : (mail.body_text || null);
    this.attachments = (mail.attachments ?? [])
      .map(a => ({ ...a, href: intakeAttachmentPath(mail.id, a.id) }))
      .filter(a => a.href !== null);
    this.contacts = (mail.contacts ?? []).filter(c => !!(c?.name || c?.email || c?.phone));
  }

  /** headerTitle is read before the mail loads, so the subject is added here. */
  private async titleWithSubject(subject: string): Promise<void> {
    await this.titles.newTitle([await this.translate.get('intake.title').toPromise(), subject]);
  }

  private frameDocFor: number = null;

  private async loadFrameDoc(id: number): Promise<void> {
    this.frameDocFor = id;
    this.frameDoc = null;
    this.frameFallback = false;
    const html = await this.intake.mailHtml(id).catch(() => null);
    if (this.frameDocFor !== id) { return; }
    if (html === null) { this.frameFallback = true; return; }
    // Same CSP as the server response: nothing remote loads, so Manrope applies only where installed
    // and the stack falls back to the system sans-serif instead of the browser's default serif.
    this.frameDoc = this.sanitizer.bypassSecurityTrustHtml(
      `<!doctype html><html><head><meta charset="utf-8">`
      + `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'">`
      // Theme values hardcoded: the sandboxed frame cannot read the app's CSS variables. No @font-face or url():
      // the frame loads nothing, so Manrope renders only where the system has it.
      + `<style>body { font-family: Manrope, system-ui, -apple-system, "Segoe UI", sans-serif;`
      + ` font-size: 15px; /* text-paragraph-font-size */`
      + ` line-height: 20px; /* text-paragraph-line-height */`
      + ` color: #2B3A49; /* text-basic-color */ margin: 12px; }`
      + ` table, th, td { border-color: #EFF1F4; /* border-basic-color-3: recolours only, a mail's own borders stay */ }</style>`
      + `</head><body>${html}</body></html>`);
  }

  async deleteMail(): Promise<void> {
    if (!this.mail || !this.canDelete || this.deleting) { return; }
    this.deleting = true;
    try {
      const ok = await this.notifier.confirm(
        'intake.mail.delete_confirm_title',
        this.translate.instant('intake.mail.delete_confirm_body'),
        'intake.mail.delete_yes',
        'danger',
        'no',
      );
      if (!ok) { return; }
      // 'failed' means the stored objects survived; the row is already hidden and prune retries the wipe.
      let gone = false;
      const result = await this.intake.deleteMail(this.mail.id).catch(e => {
        // 404: someone else deleted it first. The goal is met, so say so and leave the dead page.
        if (e?.status === 404) {
          gone = true;
        } else if (INTAKE_DELETE_SILENT.includes(e?.status)) {
          this.toastr.danger(intakeErrorMessage(this.translate, e, false), this.translate.instant('intake.title'));
        }
        return null;
      });
      if (gone) {
        this.toastr.info(this.translate.instant('intake.mail.already_gone'), this.translate.instant('intake.title'));
        await this.router.navigate(['/market/intake']);
        return;
      }
      if (!result) { return; }
      if (result.content_wipe_state === 'failed') {
        this.toastr.warning(this.translate.instant('intake.mail.delete_failed'), this.translate.instant('intake.title'));
      } else {
        this.toastr.success(this.translate.instant('intake.mail.deleted'), this.translate.instant('intake.title'));
      }
      await this.router.navigate(['/market/intake']);
    } finally {
      this.deleting = false;
    }
  }

  private clear(): void {
    this.mail = null;
    this.frameUrl = null;
    this.bodyText = null;
    this.attachments = [];
    this.contacts = [];
  }
}
